/**
 * Servicio para generación de reportes de morosos
 * Incluye exportación Excel con múltiples hojas por curso
 */

import * as XLSX from 'xlsx';
import { createWordPressAPIInstance } from './billing';
import { getCredentials, getConfig } from './woo';

/**
 * Obtener datos de morosos agrupados por curso/grado
 * @param {Object} filters - Filtros de fecha y criterios
 * @returns {Promise} Datos agrupados por curso con métricas
 */
const getMonthsBetween = (startDate, endDate) => {
  const months = [];
  const current = new Date(new Date(startDate).getFullYear(), new Date(startDate).getMonth(), 1);
  const end = new Date(new Date(endDate).getFullYear(), new Date(endDate).getMonth(), 1);
  while (current <= end) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, '0');
    months.push(`${year}-${month}`);
    current.setMonth(current.getMonth() + 1);
  }
  return months;
};

export const getOverduePaymentsByGrade = async (filters = {}) => {
  try {
    const wpApi = createWordPressAPIInstance();
    
    // Obtener TODAS las órdenes pendientes usando paginación completa
    console.log('🔍 Iniciando obtención paginada de todas las órdenes pendientes...');
    
    const allPendingOrders = [];
    let page = 1;
    let hasMoreData = true;
    let totalFromResponse = 0;

    while (hasMoreData) {
      const params = {
        status: 'pending',
        per_page: 100,
        page: page,
        orderby: 'date_modified',
        order: 'desc',
        // Pasar los filtros aplicados en la vista para no recorrer toda la DB
        customer_id: filters.customer_id || '',
        start_date:  filters.start_date  || '',
        end_date:    filters.end_date    || '',
        group:       filters.group       || '',
      };

      console.log(`📄 Obteniendo página ${page} de órdenes pendientes...`);

      try {
        const response = await wpApi.get('/orders/search', { params });
        const orders = response.data.data || [];
        
        if (orders.length === 0) {
          hasMoreData = false;
          break;
        }

        allPendingOrders.push(...orders);

        // Obtener total de la primera página
        if (page === 1) {
          totalFromResponse = response.data.total || 0;
          console.log(`📊 Total órdenes pendientes disponibles: ${totalFromResponse}`);
        }

        // Verificar si hay más páginas
        const totalPages = response.data.total_pages || 1;
        console.log(`📄 Procesada página ${page}/${totalPages}, órdenes obtenidas: ${allPendingOrders.length}`);
        
        if (page >= totalPages) {
          hasMoreData = false;
        } else {
          page++;
        }

      } catch (pageError) {
        console.error(`❌ Error obteniendo página ${page}:`, pageError);
        hasMoreData = false;
      }
    }

    console.log(`✅ Total órdenes pendientes obtenidas: ${allPendingOrders.length}`);

    // Determinar rango de meses: usar filtros aplicados o año escolar por defecto
    let validMonths;
    if (filters.start_date && filters.end_date) {
      validMonths = getMonthsBetween(filters.start_date, filters.end_date);
      console.log(`📅 Usando rango de filtros: ${filters.start_date} a ${filters.end_date} → ${validMonths.length} meses`);
    } else {
      const currentYear = new Date().getFullYear();
      const schoolYear = new Date().getMonth() < 2 ? currentYear - 1 : currentYear;
      validMonths = [
        `${schoolYear}-03`, `${schoolYear}-04`, `${schoolYear}-05`, `${schoolYear}-06`,
        `${schoolYear}-07`, `${schoolYear}-08`, `${schoolYear}-09`, `${schoolYear}-10`,
        `${schoolYear}-11`, `${schoolYear}-12`
      ];
      console.log(`📅 Año escolar detectado: ${schoolYear} (marzo a diciembre)`);
    }

    console.log('📅 Revisando mensualidades:', validMonths);

    // Filtrar solo órdenes que corresponden a mensualidades vencidas
    const overdueOrders = filterOverdueMonthlyPayments(allPendingOrders, validMonths);
    console.log(`💸 Mensualidades realmente vencidas: ${overdueOrders.length}`);

    // Procesar y agrupar por curso
    const overdueByGrade = groupOrdersByGrade(overdueOrders);
    
    // Calcular métricas globales
    const globalMetrics = calculateGlobalMetrics(overdueByGrade);
    
    return {
      success: true,
      data: overdueByGrade,
      metrics: globalMetrics,
      total_orders: overdueOrders.length,
      export_date: new Date().toISOString(),
      period: {
        start: validMonths[0] + '-01',
        end: validMonths[validMonths.length - 1] + '-01'
      }
    };

  } catch (error) {
    console.error('Error obteniendo morosos por grado:', error);
    throw new Error('Error al obtener datos de morosos: ' + error.message);
  }
};

/**
 * Filtrar órdenes por mensualidades realmente vencidas
 * Implementa el patrón mes por mes como el generador de multas
 * @param {Array} allOrders - Todas las órdenes pendientes
 * @param {Array} schoolMonths - Array de meses del año escolar (YYYY-MM format)
 * @returns {Array} Solo órdenes con mensualidades realmente vencidas
 */
const filterOverdueMonthlyPayments = (allOrders, schoolMonths) => {
  const overdueOrders = [];
  const currentDate = new Date();
  
  console.log(`🔍 Filtrando ${allOrders.length} órdenes por mensualidades vencidas...`);

  allOrders.forEach((order, index) => {
    try {
      // Excluir becados y suscriptores archivados del reporte de morosos
      const customerRole = order.customer_info?.role || '';
      if (customerRole.includes('becado') || customerRole.includes('subscriber')) {
        console.log(`⏭️ Orden #${order.id}: cliente ${customerRole}, excluido del reporte de morosos`);
        return;
      }

      // Obtener mes desde meta_data
      let orderMonth = null;
      if (order.meta_data && Array.isArray(order.meta_data)) {
        const mesMeta = order.meta_data.find(meta => meta.key === 'mes');
        if (mesMeta && mesMeta.value) {
          let mesValue = mesMeta.value.toString();
          
          // Normalizar formato: si viene "2025-12-31", convertir a "2025-12"
          if (mesValue.length === 10 && mesValue.includes('-')) {
            // Formato fecha completa: "2025-12-31" -> "2025-12"
            orderMonth = mesValue.substring(0, 7);
          } else if (mesValue.length === 7 && mesValue.includes('-')) {
            // Formato ya correcto: "2025-12"
            orderMonth = mesValue;
          } else {
            console.warn(`⚠️ Orden #${order.id}: Formato de mes no reconocido: ${mesValue}`);
          }
        }
      }

      // Obtener fecha_vencimiento desde meta_data
      let fechaVencimiento = null;
      if (order.meta_data && Array.isArray(order.meta_data)) {
        const vencimientoMeta = order.meta_data.find(meta => meta.key === 'fecha_vencimiento');
        if (vencimientoMeta && vencimientoMeta.value) {
          fechaVencimiento = vencimientoMeta.value; // Formato: "2025-03-15"
        }
      }

      // Validación 1: Debe tener mes válido del año escolar
      if (!orderMonth || !schoolMonths.includes(orderMonth)) {
        console.log(`⏭️ Orden #${order.id}: Mes no válido (${orderMonth}), saltando`);
        return; // Skip esta orden
      }

      // Validación 2: Debe ser concepto de mensualidad (no multa ni otros conceptos)
      let orderConcept = null;
      if (order.meta_data && Array.isArray(order.meta_data)) {
        const conceptMeta = order.meta_data.find(meta => meta.key === 'concepto');
        if (conceptMeta && conceptMeta.value) {
          orderConcept = conceptMeta.value.toString().toLowerCase();
        }
      }

      // Solo procesar mensualidades, excluir multas y otros conceptos
      if (!orderConcept || !orderConcept.includes('mensualidad')) {
        console.log(`⏭️ Orden #${order.id}: No es mensualidad (${orderConcept}), saltando`);
        return; // Skip esta orden
      }

      // Excluir explícitamente multas
      if (orderConcept.includes('multa') || orderConcept.includes('mora') || orderConcept.includes('recargo')) {
        console.log(`⏭️ Orden #${order.id}: Es multa/mora/recargo (${orderConcept}), saltando`);
        return; // Skip esta orden
      }

      // Validación 3: Si tiene fecha_vencimiento, usarla para calcular atraso
      let isOverdue = false;
      let daysOverdue = 0;
      let calculationSource = 'date_created';

      if (fechaVencimiento) {
        try {
          const vencimientoDate = new Date(fechaVencimiento);
          const diffTime = currentDate.getTime() - vencimientoDate.getTime();
          daysOverdue = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          calculationSource = 'fecha_vencimiento';
          
          // Solo está vencida si han pasado días positivos desde fecha_vencimiento
          isOverdue = daysOverdue > 0;
          
          console.log(`📅 Orden #${order.id}: ${orderMonth} vence ${fechaVencimiento}, días atraso: ${daysOverdue}`);
        } catch (error) {
          console.warn(`⚠️ Orden #${order.id}: fecha_vencimiento inválida (${fechaVencimiento}), usando date_created`);
          // Fallback a date_created si fecha_vencimiento es inválida
        }
      }

      // Fallback: Si no hay fecha_vencimiento válida, usar date_created
      if (!isOverdue && calculationSource === 'date_created') {
        try {
          const createdDate = new Date(order.date_created);
          const diffTime = currentDate.getTime() - createdDate.getTime();
          daysOverdue = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          
          // Para fallback, considerar vencida solo si han pasado más de 30 días
          isOverdue = daysOverdue > 30;
          
          console.log(`📝 Orden #${order.id}: ${orderMonth} creada ${order.date_created}, días desde creación: ${daysOverdue}`);
        } catch (error) {
          console.error(`❌ Orden #${order.id}: Error procesando fechas`, error);
          return; // Skip esta orden si no se pueden procesar fechas
        }
      }

      // Solo incluir si está realmente vencida
      if (isOverdue && daysOverdue > 0) {
        // Agregar metadata de cálculo a la orden para uso posterior
        order._overdue_calculation = {
          days_overdue: daysOverdue,
          calculated_from: calculationSource,
          order_month: orderMonth,
          fecha_vencimiento: fechaVencimiento
        };
        
        overdueOrders.push(order);
        console.log(`✅ Orden #${order.id}: INCLUIDA - ${orderMonth}, ${daysOverdue} días atraso (${calculationSource})`);
      } else {
        console.log(`⏭️ Orden #${order.id}: NO vencida - ${orderMonth}, ${daysOverdue} días (${calculationSource})`);
      }

    } catch (error) {
      console.error(`❌ Error procesando orden #${order.id}:`, error);
    }
  });

  console.log(`💸 Resultado: ${overdueOrders.length} de ${allOrders.length} órdenes están realmente vencidas`);
  return overdueOrders;
};

/**
 * Agrupar órdenes por curso/grado del alumno
 * @param {Array} orders - Lista de órdenes pendientes
 * @returns {Object} Datos agrupados por curso
 */
const groupOrdersByGrade = (orders) => {
  const groupedData = {};

  orders.forEach(order => {
    try {
      // Obtener grupo/curso del alumno desde meta_data
      let customerGroup = 'Sin Grupo Asignado';
      
      if (order.meta_data && Array.isArray(order.meta_data)) {
        // Buscar el grupo en diferentes campos de meta_data
        const groupMeta = order.meta_data.find(meta =>
          meta.key === 'group_name' ||
          meta.key === 'customer_group_name' ||
          meta.key === 'grupo' ||
          meta.key === 'curso' ||
          meta.key === 'course_name' ||
          meta.key === 'grupo_curso'
        );
        
        if (groupMeta && groupMeta.value) {
          customerGroup = groupMeta.value.toString().trim();
        }
      }

      // Normalizar nombre del grupo
      customerGroup = normalizeGradeName(customerGroup);

      // Inicializar grupo si no existe
      if (!groupedData[customerGroup]) {
        groupedData[customerGroup] = {
          gradeName: customerGroup,
          students: [],
          totalAmount: 0,
          totalOrders: 0,
          averageDaysOverdue: 0,
          criticalCases: 0 // >90 días
        };
      }

      // Usar datos de cálculo pre-procesados por filterOverdueMonthlyPayments
      const overdueCalc = order._overdue_calculation;
      if (!overdueCalc) {
        console.warn(`⚠️ Orden #${order.id}: Sin datos de cálculo de vencimiento, saltando`);
        return;
      }
      
      const daysOverdue = overdueCalc.days_overdue;
      const fechaVencimiento = overdueCalc.fecha_vencimiento;
      const calculationSource = overdueCalc.calculated_from;
      const orderMonth = overdueCalc.order_month;
      
      const amount = parseFloat(order.total) || 0;
      
      console.log(`📋 Orden #${order.id}: ${orderMonth}, ${daysOverdue} días atraso (${calculationSource})`);

      // Las órdenes ya vienen pre-filtradas como vencidas por filterOverdueMonthlyPayments

      // Buscar si el estudiante ya existe en este grupo
      let existingStudent = groupedData[customerGroup].students.find(
        student => student.email === order.billing.email
      );

      if (existingStudent) {
        // Agregar orden a estudiante existente
        existingStudent.orders.push({
          id: order.id,
          amount: amount,
          status: order.status,
          date_created: order.date_created,
          fecha_vencimiento: fechaVencimiento,
          days_overdue: daysOverdue,
          calculated_from: calculationSource,
          concept: getOrderConcept(order),
          month: orderMonth
        });
        existingStudent.total_amount += amount;
        existingStudent.total_orders++;
        existingStudent.max_days_overdue = Math.max(existingStudent.max_days_overdue, daysOverdue);
      } else {
        // Crear nuevo estudiante
        const newStudent = {
          name: `${order.billing.first_name} ${order.billing.last_name}`.trim(),
          email: order.billing.email,
          username: order.customer_info?.username || '',
          total_amount: amount,
          total_orders: 1,
          max_days_overdue: daysOverdue,
          orders: [{
            id: order.id,
            amount: amount,
            status: order.status,
            date_created: order.date_created,
            fecha_vencimiento: fechaVencimiento,
            days_overdue: daysOverdue,
            calculated_from: calculationSource,
            concept: getOrderConcept(order),
            month: orderMonth
          }]
        };
        
        groupedData[customerGroup].students.push(newStudent);
      }

      // Actualizar totales del grupo
      groupedData[customerGroup].totalAmount += amount;
      groupedData[customerGroup].totalOrders++;

    } catch (error) {
      console.error('Error procesando orden:', order.id, error);
    }
  });

  // Calcular métricas finales por grupo
  Object.keys(groupedData).forEach(gradeName => {
    const group = groupedData[gradeName];
    if (group.students.length > 0) {
      const totalDaysOverdue = group.students.reduce((sum, student) =>
        sum + student.max_days_overdue, 0
      );
      group.averageDaysOverdue = Math.round(totalDaysOverdue / group.students.length);
      // criticalCases: contar estudiantes (no órdenes) con más de 90 días de atraso
      group.criticalCases = group.students.filter(s => s.max_days_overdue >= 90).length;
    }

    // Ordenar estudiantes por días de atraso (críticos primero)
    group.students.sort((a, b) => b.max_days_overdue - a.max_days_overdue);
  });

  return groupedData;
};

/**
 * Normalizar nombres de cursos/grados a códigos cortos.
 *
 * Maneja los 3 formatos reales de la DB:
 *  - Formato prefijado:  "110:Enseñanza Básica - 1 - A"  → "1A"
 *                        "310:Enseñanza Media... - Primer Año - A" → "1MA"
 *                        "10:Educación Parvularia - Transición 1er Nivel - A" → "NT1-A"
 *  - Formato nombre:     "Primer Año Básico - A" → "1A"  (con o sin acentos)
 *  - Código ya corto:    "3MA", "4A", "PKB" → pasan directo
 *
 * Cursos del mismo nivel pero distinto formato colapsan al mismo código,
 * lo que los agrupa en una sola hoja del Excel.
 */
const normalizeGradeName = (gradeName) => {
  if (!gradeName || gradeName === 'Sin Grupo Asignado') return 'Sin Grupo Asignado';

  // Mapa de nombres exactos como fallback rápido
  const gradeMap = {
    'Pre Kinder A': 'PKA', 'Pre Kinder B': 'PKB',
    'Kinder A': 'KA', 'Kinder B': 'KB',
    'Primer Año Básico - A': '1A',   'Primer Año Básico - B': '1B',
    'Segundo Año Básico - A': '2A',  'Segundo Año Básico - B': '2B',
    'Tercer Año Básico - A': '3A',   'Tercer Año Básico - B': '3B',
    'Cuarto Año Básico - A': '4A',   'Cuarto Año Básico - B': '4B',
    'Quinto Año Básico - A': '5A',   'Quinto Año Básico - B': '5B',
    'Sexto Año Básico - A': '6A',    'Sexto Año Básico - B': '6B',
    'Septimo Año Básico - A': '7A',  'Septimo Año Básico - B': '7B',
    'Octavo Año Básico - A': '8A',   'Octavo Año Básico - B': '8B',
    'Primer Año Medio - A': '1MA',   'Primer Año Medio - B': '1MB',
    'Segundo Año Medio - A': '2MA',  'Segundo Año Medio - B': '2MB',
    'Tercer Año Medio - A': '3MA',   'Tercer Año Medio - B': '3MB',
    'Cuarto Año Medio - A': '4MA',   'Cuarto Año Medio - B': '4MB',
  };

  // Paso 1: match exacto
  if (gradeMap[gradeName]) return gradeMap[gradeName];

  // Normalización: quita acentos, indicadores ordinales (º °), minúsculas, espacios múltiples
  const norm = (s) =>
    s.normalize('NFD')
     .replace(/[\u0300-\u036f]/g, '')
     .replace(/[º°]/g, 'o')
     .toLowerCase()
     .replace(/\s+/g, ' ')
     .trim();

  const cleaned = norm(gradeName);

  // Paso 2: match con claves del mapa normalizadas (cubre variantes de acentuación)
  for (const [key, val] of Object.entries(gradeMap)) {
    if (norm(key) === cleaned) return val;
  }

  // Paso 3: códigos cortos ya formateados en la DB ("3MA", "4A", "PKB", "KA", etc.)
  const trimmed = gradeName.trim();
  if (/^(nt[12]-?[ab]|pk[ab]|k[ab])$/i.test(trimmed)) return trimmed.toUpperCase();
  if (/^\d+[mb]?[ab]$/i.test(trimmed)) return trimmed.toUpperCase();

  // Paso 4: extracción por componentes
  // Detectar y separar el prefijo numérico: "110:Enseñanza Básica - ..."
  //   10  = Educación Parvularia
  //   110 = Enseñanza Básica
  //   310 = Enseñanza Media
  const prefixMatch = cleaned.match(/^(\d+):(.+)/);
  let workStr = cleaned;
  let prefixCode = null;
  if (prefixMatch) {
    prefixCode = parseInt(prefixMatch[1]);
    workStr = prefixMatch[2].trim();
  }

  // Letra al final del string (precedida por guión o espacio)
  const letterMatch = workStr.match(/[-\s]+([ab])\s*$/i);
  const letter = letterMatch ? letterMatch[1].toUpperCase() : null;

  // ── Parvularia (prefixCode 10 o contiene "parvularia") ──────────────────
  if (prefixCode === 10 || /parvularia/.test(workStr)) {
    // Transición 1er Nivel o edad 4 → NT1
    if (/transicion\s*(1|1er|primer)|[-\s]+4\s*[-\s]/.test(workStr)) {
      return letter ? `NT1-${letter}` : 'NT1';
    }
    // Transición 2º Nivel o edad 5 → NT2
    if (/transicion\s*(2|2o|segundo)|[-\s]+5\s*[-\s]/.test(workStr)) {
      return letter ? `NT2-${letter}` : 'NT2';
    }
    return letter ? `PARV-${letter}` : 'PARV';
  }

  // ── Pre-Kinder / Kinder sin prefijo ─────────────────────────────────────
  if (/pre[\s-]*kinder/.test(workStr)) return letter ? `PK${letter}` : 'PK';
  if (/^kinder/.test(workStr))         return letter ? `K${letter}`  : 'K';

  // ── Básica / Media ───────────────────────────────────────────────────────
  // isMedia: por prefijo 310, o porque el string menciona "media/medio/humanist"
  const isMedia = prefixCode === 310 ||
    /\bmedio\b|\bmedia\b|humanist|cientifico/.test(workStr);

  // Número de grado: dígito en formato "- N -", dígito al inicio, o palabra
  const numMap = [
    { words: ['primer', 'primero', 'primera'], num: 1 },
    { words: ['segundo', 'segunda'],           num: 2 },
    { words: ['tercer', 'tercero', 'tercera'], num: 3 },
    { words: ['cuarto', 'cuarta'],             num: 4 },
    { words: ['quinto', 'quinta'],             num: 5 },
    { words: ['sexto', 'sexta'],               num: 6 },
    { words: ['septimo', 'septima'],           num: 7 },
    { words: ['octavo', 'octava'],             num: 8 },
  ];

  let gradeNum = null;
  // Dígito rodeado de guiones/espacios: "- 3 -" (formato numérico del prefijado)
  const midDigit = workStr.match(/[-\s]+(\d)\s*[-\s]+[ab]/i);
  if (midDigit) gradeNum = parseInt(midDigit[1]);
  // Dígito al comienzo del string
  if (!gradeNum) {
    const startDigit = workStr.match(/^(\d)/);
    if (startDigit) gradeNum = parseInt(startDigit[1]);
  }
  // Palabra numérica en cualquier posición del string
  if (!gradeNum) {
    for (const { words, num } of numMap) {
      if (words.some(w => workStr.includes(w))) { gradeNum = num; break; }
    }
  }

  if (!gradeNum || !letter) return gradeName; // no se pudo determinar, devolver original

  return isMedia ? `${gradeNum}M${letter}` : `${gradeNum}${letter}`;
};


/**
 * Convertir mes formato YYYY-MM a nombre legible
 * @param {string} monthCode - Mes en formato "2025-03"
 * @returns {string} Nombre del mes "Marzo 2025"
 */
const getMonthName = (monthCode) => {
  if (!monthCode || typeof monthCode !== 'string') return 'Mes no definido';
  
  const [year, month] = monthCode.split('-');
  
  const monthNames = {
    '01': 'Enero', '02': 'Febrero', '03': 'Marzo', '04': 'Abril',
    '05': 'Mayo', '06': 'Junio', '07': 'Julio', '08': 'Agosto',
    '09': 'Septiembre', '10': 'Octubre', '11': 'Noviembre', '12': 'Diciembre'
  };
  
  return `${monthNames[month] || 'Mes'} ${year}`;
};

/**
 * Extraer concepto del pago desde meta_data
 * @param {Object} order - Orden de WooCommerce
 * @returns {string} Concepto del pago
 */
const getOrderConcept = (order) => {
  if (!order.meta_data) return 'Pago mensualidad';
  
  const conceptMeta = order.meta_data.find(meta => 
    meta.key === 'concepto' || 
    meta.key === 'concept' ||
    meta.key === 'description'
  );
  
  return conceptMeta ? conceptMeta.value : 'Pago mensualidad';
};

/**
 * Extraer mes del pago desde meta_data
 * @param {Object} order - Orden de WooCommerce
 * @returns {string} Mes del pago (YYYY-MM)
 */
const getOrderMonth = (order) => {
  if (!order.meta_data) return new Date(order.date_created).toISOString().substring(0, 7);
  
  const monthMeta = order.meta_data.find(meta => 
    meta.key === 'mes' || 
    meta.key === 'month' ||
    meta.key === 'periodo'
  );
  
  return monthMeta ? monthMeta.value : new Date(order.date_created).toISOString().substring(0, 7);
};

/**
 * Calcular métricas globales del reporte
 * @param {Object} overdueByGrade - Datos agrupados por grado
 * @returns {Object} Métricas globales
 */
const calculateGlobalMetrics = (overdueByGrade) => {
  let totalStudents = 0;
  let totalAmount = 0;
  let totalOrders = 0;
  let criticalCases = 0;
  let gradeCount = 0;

  const distributionByDays = {
    '1-30': 0,
    '31-60': 0,
    '61-90': 0,
    '90+': 0
  };

  Object.values(overdueByGrade).forEach(grade => {
    gradeCount++;
    totalStudents += grade.students.length;
    totalAmount += grade.totalAmount;
    totalOrders += grade.totalOrders;
    criticalCases += grade.criticalCases;

    // Distribuir por días de atraso
    grade.students.forEach(student => {
      const days = student.max_days_overdue;
      if (days <= 30) distributionByDays['1-30']++;
      else if (days <= 60) distributionByDays['31-60']++;
      else if (days <= 90) distributionByDays['61-90']++;
      else distributionByDays['90+']++;
    });
  });

  return {
    total_students: totalStudents,
    total_amount: totalAmount,
    total_orders: totalOrders,
    critical_cases: criticalCases,
    grades_affected: gradeCount,
    average_amount_per_student: totalStudents > 0 ? totalAmount / totalStudents : 0,
    distribution_by_days: distributionByDays,
    critical_percentage: totalStudents > 0 ? (criticalCases / totalStudents) * 100 : 0
  };
};

/**
 * Formatear montos en pesos chilenos
 * @param {number} amount - Monto a formatear
 * @returns {string} Monto formateado
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Crear hoja de resumen ejecutivo
 * @param {Object} reportData - Datos completos del reporte
 * @returns {Object} Worksheet de resumen
 */
const createSummaryWorksheet = (reportData) => {
  const { data: overdueByGrade, metrics, export_date, period } = reportData;
  
  const summaryData = [
    ['REPORTE LISTADO DE MOROSOS'],
    [''],
    ['INFORMACIÓN GENERAL'],
    ['Fecha de exportación:', new Date(export_date).toLocaleString('es-CL')],
    ['Período analizado:', `${period.start} al ${period.end}`],
    [''],
    ['RESUMEN EJECUTIVO'],
    ['Total estudiantes morosos:', metrics.total_students],
    ['Total cursos afectados:', metrics.grades_affected],
    ['Total órdenes pendientes:', metrics.total_orders],
    ['Monto total adeudado:', formatCurrency(metrics.total_amount)],
    ['Promedio por estudiante:', formatCurrency(metrics.average_amount_per_student)],
    [''],
    ['CASOS CRÍTICOS'],
    ['Estudiantes con +90 días atraso:', metrics.critical_cases],
    ['Porcentaje crítico:', `${metrics.critical_percentage.toFixed(1)}%`],
    [''],
    ['DISTRIBUCIÓN POR DÍAS DE ATRASO'],
    ['1-30 días:', metrics.distribution_by_days['1-30']],
    ['31-60 días:', metrics.distribution_by_days['31-60']],
    ['61-90 días:', metrics.distribution_by_days['61-90']],
    ['Más de 90 días:', metrics.distribution_by_days['90+']],
    [''],
    ['RESUMEN POR CURSO'],
    ['Curso', 'Estudiantes', 'Monto Adeudado', 'Promedio Días', 'Casos Críticos']
  ];

  // Agregar datos de cada curso
  Object.keys(overdueByGrade)
    .sort()
    .forEach(gradeName => {
      const grade = overdueByGrade[gradeName];
      summaryData.push([
        gradeName,
        grade.students.length,
        formatCurrency(grade.totalAmount),
        grade.averageDaysOverdue,
        grade.criticalCases
      ]);
    });

  const ws = XLSX.utils.aoa_to_sheet(summaryData);

  // Configurar anchos de columna
  ws['!cols'] = [
    { wch: 25 }, // Etiquetas
    { wch: 15 }, // Valores
    { wch: 15 }, // Monto
    { wch: 12 }, // Días
    { wch: 12 }  // Críticos
  ];

  // Aplicar formato a encabezados
  const headerRow = summaryData.length - 1;
  for (let col = 0; col < 5; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: headerRow, c: col });
    if (!ws[cellAddress]) continue;
    
    ws[cellAddress].s = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "366092" } },
      alignment: { horizontal: "center", vertical: "center" }
    };
  }

  return ws;
};

/**
 * Crear hoja de datos por curso/grado
 * @param {Object} gradeData - Datos del curso específico
 * @param {string} gradeName - Nombre del curso
 * @returns {Object} Worksheet del curso
 */
const createGradeWorksheet = (gradeData, gradeName) => {
  const worksheetData = [
    ['LISTADO DE MOROSOS - ' + gradeName],
    [''],
    // Sin encabezados - formato libre como el archivo Excel original
  ];

  // Agregar datos de cada estudiante con formato: Nombre + detalle debajo
  gradeData.students.forEach((student, index) => {
    // Fila con nombre del estudiante y resumen
    const totalMeses = [...new Set(student.orders.map(order => order.month))].length;
    
    const rutLabel = student.username ? ` | RUT: ${student.username}` : '';
    worksheetData.push([
      `${student.name} (${student.email})${rutLabel}`,
      '',
      `${totalMeses} meses adeudados`,
      `${student.max_days_overdue} días atraso`,
      formatCurrency(student.total_amount),
      '',
      ''
    ]);

    // Filas con detalle de cada orden vencida
    student.orders
      .sort((a, b) => new Date(a.month + '-01') - new Date(b.month + '-01')) // Ordenar por mes
      .forEach(order => {
        const monthName = getMonthName(order.month); // ej: "Marzo 2025"
        const vencimientoDate = order.fecha_vencimiento || 'No definida';
        
        worksheetData.push([
          '', // Columna vacía para indentar
          `Orden #${order.id}`,
          `${monthName}`,
          `Vence: ${vencimientoDate}`,
          formatCurrency(order.amount),
          `${order.days_overdue} días`,
          order.concept || 'Mensualidad'
        ]);
      });

    // Línea separadora entre estudiantes (excepto el último)
    if (index < gradeData.students.length - 1) {
      worksheetData.push(['', '', '', '', '', '', '']);
    }
  });

  // Agregar fila de totales
  worksheetData.push([
    '',
    '',
    '',
    '',
    '',
    '',
    ''
  ]);
  worksheetData.push([
    'TOTALES CURSO:',
    `${gradeData.students.length} estudiantes`,
    '',
    `${gradeData.averageDaysOverdue} días promedio`,
    formatCurrency(gradeData.totalAmount),
    gradeData.totalOrders,
    `${gradeData.criticalCases} casos críticos`
  ]);

  const ws = XLSX.utils.aoa_to_sheet(worksheetData);

  // Configurar anchos de columna para el nuevo formato
  ws['!cols'] = [
    { wch: 45 }, // Nombre del estudiante o detalle de orden
    { wch: 20 }, // Orden #
    { wch: 18 }, // Mes
    { wch: 20 }, // Fecha vencimiento
    { wch: 15 }, // Monto
    { wch: 12 }, // Días atraso
    { wch: 25 }  // Concepto
  ];

  // Aplicar formato especial a nombres de estudiantes (filas principales)
  // Detectar filas de estudiantes (tienen email en el nombre)
  for (let row = 2; row < worksheetData.length; row++) {
    const rowData = worksheetData[row];
    if (rowData && rowData[0] && rowData[0].includes('@')) {
      // Es una fila de estudiante - aplicar formato destacado
      for (let col = 0; col < 7; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!ws[cellAddress]) continue;
        
        ws[cellAddress].s = {
          font: { bold: true, color: { rgb: "000080" } },
          fill: { fgColor: { rgb: "F0F8FF" } },
          alignment: { horizontal: "left", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "CCCCCC" } },
            bottom: { style: "thin", color: { rgb: "CCCCCC" } },
            left: { style: "thin", color: { rgb: "CCCCCC" } },
            right: { style: "thin", color: { rgb: "CCCCCC" } }
          }
        };
      }
    }
  }

  // Aplicar formato simple a filas de detalle de órdenes (indentadas)
  for (let row = 2; row < worksheetData.length - 2; row++) {
    const rowData = worksheetData[row];
    if (rowData && rowData[0] === '' && rowData[1] && rowData[1].includes('Orden #')) {
      // Es una fila de detalle de orden - aplicar formato sutil
      for (let col = 0; col < 7; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!ws[cellAddress]) continue;

        ws[cellAddress].s = {
          alignment: { horizontal: "left", vertical: "center" },
          font: { size: 10 },
          border: {
            left: { style: "thin", color: { rgb: "E8E8E8" } }
          }
        };

        // Resaltar casos críticos en días de atraso
        if (col === 5 && ws[cellAddress].v && parseInt(ws[cellAddress].v) >= 90) {
          ws[cellAddress].s.fill = { fgColor: { rgb: "FFE6E6" } };
          ws[cellAddress].s.font = { color: { rgb: "CC0000" }, bold: true, size: 10 };
        }
      }
    }
  }

  // Formato especial para fila de totales
  const totalRow = worksheetData.length - 1;
  for (let col = 0; col < 7; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: totalRow, c: col });
    if (!ws[cellAddress]) continue;
    
    ws[cellAddress].s = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "4A5568" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thick" },
        bottom: { style: "thick" },
        left: { style: "thin" },
        right: { style: "thin" }
      }
    };
  }

  // No configurar auto-filtro ya que el formato es libre sin encabezados fijos

  return ws;
};

/**
 * Generar nombre de archivo para el reporte de morosos
 * @param {Object} period - Período del reporte
 * @returns {string} Nombre del archivo
 */
const generateOverdueReportFilename = (period) => {
  const currentDate = new Date().toISOString().split('T')[0].replace(/-/g, '');
  const startMonth = period.start.substring(0, 7).replace('-', '');
  return `LISTADO_MOROSOS_${startMonth}_AL_${currentDate}.xlsx`;
};

/**
 * Descargar archivo Excel del reporte de morosos
 * @param {Object} workbook - Workbook de Excel
 * @param {string} filename - Nombre del archivo
 */
const downloadOverdueExcelFile = (workbook, filename) => {
  // Generar archivo Excel binario
  const excelBuffer = XLSX.write(workbook, { 
    bookType: 'xlsx', 
    type: 'array',
    compression: true 
  });
  
  // Crear blob y descargar
  const blob = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Exportar reporte de morosos a Excel con múltiples hojas
 * @param {Object} filters - Filtros de fecha y criterios
 * @returns {Promise} Resultado de la exportación
 */
export const exportOverdueToExcel = async (filters = {}) => {
  try {
    console.log('📊 Iniciando exportación de listado de morosos...');

    // Obtener datos de morosos agrupados por curso
    const reportData = await getOverduePaymentsByGrade(filters);
    
    if (!reportData.success) {
      throw new Error('No se pudieron obtener los datos de morosos');
    }

    const { data: overdueByGrade, metrics } = reportData;

    // Crear nuevo workbook
    const wb = XLSX.utils.book_new();

    // 1. Crear y agregar hoja de resumen
    console.log('📋 Creando hoja de resumen ejecutivo...');
    const summaryWs = createSummaryWorksheet(reportData);
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Resumen');

    // 2. Crear hojas para cada curso/grado (ordenadas alfabéticamente)
    const gradeNames = Object.keys(overdueByGrade).sort();
    
    console.log(`📚 Creando hojas para ${gradeNames.length} cursos...`);
    gradeNames.forEach(gradeName => {
      const gradeData = overdueByGrade[gradeName];
      
      if (gradeData.students.length > 0) {
        console.log(`  ➤ Procesando ${gradeName}: ${gradeData.students.length} estudiantes`);
        
        const gradeWs = createGradeWorksheet(gradeData, gradeName);
        
        // Normalizar nombre de hoja para Excel (máximo 31 caracteres, sin caracteres especiales)
        let sheetName = gradeName
          .replace(/[\\\/\*\[\]:?]/g, '') // Remover caracteres especiales
          .replace(/[^\w\s-]/g, '') // Solo permitir letras, números, espacios y guiones
          .trim()
          .substring(0, 31);
          
        // Asegurar que no hay nombres duplicados
        let finalSheetName = sheetName;
        let counter = 1;
        while (wb.SheetNames.includes(finalSheetName)) {
          const suffix = `_${counter}`;
          finalSheetName = sheetName.substring(0, 31 - suffix.length) + suffix;
          counter++;
        }
        
        XLSX.utils.book_append_sheet(wb, gradeWs, finalSheetName);
      }
    });

    // 3. Generar nombre de archivo y descargar
    const filename = generateOverdueReportFilename(reportData.period);
    console.log('💾 Descargando archivo:', filename);
    
    downloadOverdueExcelFile(wb, filename);

    console.log('✅ Exportación completada exitosamente');

    return {
      success: true,
      filename,
      recordCount: metrics.total_students,
      gradesCount: metrics.grades_affected,
      totalAmount: metrics.total_amount,
      criticalCases: metrics.critical_cases,
      format: 'Excel (Múltiples hojas)',
      period: reportData.period
    };

  } catch (error) {
    console.error('❌ Error exportando listado de morosos:', error);
    throw new Error('Error al exportar listado de morosos: ' + error.message);
  }
};

export default {
  getOverduePaymentsByGrade,
  exportOverdueToExcel,
  formatCurrency
};